import { useEffect, useState } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine, Cell } from "recharts";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fmtDur = (m) => `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}m`;
const TYPE_COLOR = { "S/R": "#64748b", CHARLA: "#0f172a", FALLA: "#b91c1c", DEMORA: "#d97706", ALIMENTOS: "#2563eb", MANTTO: "#7c3aed" };
const TYPE_LABEL = { "S/R": "Standby", CHARLA: "Charla", FALLA: "Falla", DEMORA: "Demora", ALIMENTOS: "Alimentos", MANTTO: "Mantto" };

export default function TimeStudyPareto({ refreshKey }) {
  const [groupBy, setGroupBy] = useState("cause");
  const [filters, setFilters] = useState({ area: "", operator: "", date_from: "", date_to: "" });
  const [data, setData] = useState({ items: [], lost_min: 0, total_min: 0, studies: 0 });

  useEffect(() => {
    const p = { group_by: groupBy };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    api.get("/operations/time-studies/pareto", { params: p }).then((r) => setData(r.data)).catch(() => {});
  }, [groupBy, filters, refreshKey]);

  const rows = data.items.slice(0, 15).map((it, i) => ({ ...it, idx: i + 1, label: groupBy === "type" ? (TYPE_LABEL[it.type] || it.type) : it.cause }));
  const vital = rows.filter((r) => r.cum_pct <= 80).length || (rows.length ? 1 : 0);

  return (
    <div className="space-y-3" data-testid="ts-pareto">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex gap-1">
          {[["cause", "Por causa"], ["type", "Por código"]].map(([g, l]) => (
            <Button key={g} size="sm" variant={groupBy === g ? "default" : "outline"} className="h-7 text-xs" onClick={() => setGroupBy(g)} data-testid={`ts-pareto-group-${g}`}>{l}</Button>
          ))}
        </div>
        <Input placeholder="Frente" className="h-7 w-32 text-xs" value={filters.area} onChange={(e) => setFilters((f) => ({ ...f, area: e.target.value }))} data-testid="ts-pareto-area" />
        <Input placeholder="Operador" className="h-7 w-32 text-xs" value={filters.operator} onChange={(e) => setFilters((f) => ({ ...f, operator: e.target.value }))} data-testid="ts-pareto-operator" />
        <Input type="date" className="h-7 w-36 text-xs" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="ts-pareto-from" />
        <Input type="date" className="h-7 w-36 text-xs" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="ts-pareto-to" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        {[["Estudios", data.studies], ["Tiempo perdido", fmtDur(data.lost_min)], ["% del total", data.total_min ? `${Math.round(data.lost_min / data.total_min * 100)}%` : "0%"], ["Causas vitales (80%)", `${vital} de ${data.items.length}`]].map(([l, v]) => (
          <div key={l} className="border border-black p-2"><div className="text-[10px] uppercase text-slate-500">{l}</div><div className="font-black text-base" data-testid={`ts-pareto-kpi-${l.split(" ")[0].toLowerCase()}`}>{v}</div></div>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="text-center text-slate-400 text-xs py-8">Sin tiempo no productivo registrado en los estudios guardados.</p>
      ) : (
        <>
          <div className="h-72 border border-black p-2" data-testid="ts-pareto-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rows} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="idx" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="min" tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 60)}h`} />
                <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v, n) => n === "cum_pct" ? [`${v}%`, "Acumulado"] : [fmtDur(v), "Minutos"]} labelFormatter={(i) => rows[i - 1]?.label} contentStyle={{ fontSize: 11 }} />
                <ReferenceLine yAxisId="pct" y={80} stroke="#b91c1c" strokeDasharray="4 4" />
                <Bar yAxisId="min" dataKey="minutes" radius={[2, 2, 0, 0]}>
                  {rows.map((r) => <Cell key={r.idx} fill={TYPE_COLOR[r.type] || "#0f172a"} />)}
                </Bar>
                <Line yAxisId="pct" type="monotone" dataKey="cum_pct" stroke="#000" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <table className="w-full text-xs border" data-testid="ts-pareto-table">
            <thead className="bg-slate-100"><tr>{["#", "Código", "Causa", "Eventos", "Minutos", "%", "% Acum."].map((h) => <th key={h} className="px-2 py-1 text-left font-semibold">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.idx} className={`border-t ${r.cum_pct <= 80 ? "bg-red-50" : ""}`}>
                  <td className="px-2 py-1">{r.idx}</td>
                  <td className="px-2 py-1"><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 align-middle" style={{ background: TYPE_COLOR[r.type] }} />{TYPE_LABEL[r.type] || r.type}</td>
                  <td className="px-2 py-1 capitalize">{r.label}</td>
                  <td className="px-2 py-1">{r.events}</td>
                  <td className="px-2 py-1 font-semibold">{fmtDur(r.minutes)}</td>
                  <td className="px-2 py-1">{r.pct}%</td>
                  <td className="px-2 py-1 font-bold">{r.cum_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-slate-500">Las filas sombreadas concentran el 80% del tiempo perdido (causas vitales). Línea roja = umbral 80%.</p>
        </>
      )}
    </div>
  );
}
